import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { LockChangedPayload, Notification } from 'app/layout/common/notifications/notifications.types';
import { finalize, map, switchMap, take, tap } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { environment } from 'environments/environment';
import { BaseRespone, PaginationResult } from 'app/core/models/common.response';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject(null);
    private hubConnection: HubConnection;

    // Pagination tracking
    private _currentPage = 0;
    private _totalPages = 0;
    private _pageSize = 3;
    private _loading = false;
    private _allNotifications: Notification[] = [];
    lockChanged$: signalR.Subject<LockChangedPayload> = new signalR.Subject<LockChangedPayload>();

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
        private _ngZone: NgZone
    ) {
        this.setupSignalRConnection();
        // Use setTimeout to ensure SignalR has time to connect before making HTTP requests
        setTimeout(() => {
            // Load initial notifications after a brief delay
            this.refreshNotifications().subscribe(
                response => console.log('Notifications loaded successfully'),
                error => console.error('Failed to load notifications:', error)
            );
        }, 1000);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    /**
     * Getter for unread count
     */
    get unreadCount$(): Observable<number> {
        return this._unreadCount.asObservable();
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<PaginationResult> {
        return this._pagination.asObservable();
    }

    /**
     * Check if more notifications can be loaded
     */
    get canLoadMore(): boolean {
        return this._pagination.value &&
            this._currentPage < this._pagination.value.pageCount - 1;
    }

    /**
     * Check if notifications are currently loading
     */
    get isLoading(): boolean {
        return this._loading;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
 * Load notifications with pagination
 *
 * @param page Page number (0-based)
 * @param pageSize Items per page
 * @param reset Whether to reset current notifications
 */
    loadNotifications(page: number = 0, pageSize: number = 10, reset: boolean = false):
        Observable<BaseRespone<{ pagination: PaginationResult; notifications: Notification[] }>> {

        if (this._loading) {
            return of(null);
        }

        this._loading = true;

        // Create filter object similar to InwardsService pattern
        const filter = {
            pageNumber: page,
            pageSize: pageSize,
        };

        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; notifications: Notification[] }>>(
            `${environment.baseUrl}/Notification/list`, // Updated URL path with /api/ prefix
            filter
        ).pipe(
            tap((response) => {
                if (response && response.isSuccess) {
                    this._pagination.next(response.result.pagination);
                    this._currentPage = response.result.pagination.currentPage;
                    this._pageSize = response.result.pagination.pageSize;

                    // Update notifications based on reset flag
                    if (reset) {
                        this._allNotifications = [...response.result.notifications];
                    } else {
                        this._allNotifications = [...this._allNotifications, ...response.result.notifications];
                    }

                    // Update notifications and unread count
                    this._notifications.next(this._allNotifications);
                    this.updateUnreadCount();
                }
            }),
            finalize(() => {
                this._loading = false;
            })
        );
    }

    /**
      * Load more notifications (next page)
      */
    loadMore(): Observable<BaseRespone<{ pagination: PaginationResult; notifications: Notification[] }>> {
        if (!this.canLoadMore || this._loading) {
            return of(null);
        }

        return this.loadNotifications(this._currentPage + 1, this._pageSize);
    }

    /**
     * Refresh notifications (reset to first page)
     */
    refreshNotifications(): Observable<BaseRespone<{ pagination: PaginationResult; notifications: Notification[] }>> {
        return this.loadNotifications(0, this._pageSize, true);
    }


    /**
     * Mark notification as read
     *
     * @param id Notification ID
     */
    markAsRead(id: number): Observable<BaseRespone<boolean>> {
        return this._httpClient.put<BaseRespone<boolean>>(`${environment.baseUrl}/notification/${id}/read`, {})
            .pipe(
                tap((response) => {
                    if (response && response.isSuccess) {
                        // Update local notification status
                        const notification = this._allNotifications.find(n => n.id === id);
                        if (notification) {
                            notification.isRead = true;
                            this._notifications.next([...this._allNotifications]);
                            this.updateUnreadCount();
                        }
                    }
                })
            );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<BaseRespone<boolean>> {
        return this._httpClient.put<BaseRespone<boolean>>(`${environment.baseUrl}/notification/readAll`, {})
            .pipe(
                tap((response) => {
                    if (response && response.isSuccess) {
                        // Update all notifications locally
                        this._allNotifications.forEach(notification => {
                            notification.isRead = true;
                        });

                        this._notifications.next([...this._allNotifications]);
                        this._unreadCount.next(0);
                    }
                })
            );
    }



    /**
     * Setup SignalR connection
     */
    private setupSignalRConnection(): void {
        console.log('Setting up SignalR connection');

        try {
            const hubUrl = environment.baseUrl.substring(0, environment.baseUrl.lastIndexOf('/api')) + '/notify';
            console.log('SignalR hub URL:', hubUrl);

            this.hubConnection = new HubConnectionBuilder()
                .configureLogging(signalR.LogLevel.Information)
                .withUrl(hubUrl)
                .withAutomaticReconnect() // Add automatic reconnection
                .build();

            this.hubConnection.start()
                .then(() => console.log('SignalR connection established successfully'))
                .catch(err => {
                    console.error('Error starting SignalR connection:', err);
                    // Still proceed with loading initial data even if SignalR fails
                    this.refreshNotifications().subscribe();
                });

            this.hubConnection.on('SendOffersToUser', (response) => {
                console.log('SignalR notification received:', response);
                if (response.notificationType == 4) {
                    this.refreshNotifications().subscribe();
                }
            });

            this.hubConnection.on('SendLockChanged', (payload: LockChangedPayload) => {
                this._ngZone.run(() => {
                    this.lockChanged$.next(payload);
                });
            });

        } catch (error) {
            console.error('Error setting up SignalR:', error);
        }
    }

    /**
     * Update unread count
     */
    private updateUnreadCount(): void {
        const count = this._allNotifications.filter(notification => !notification.isRead).length;
        this._unreadCount.next(count);
    }
}
